import { createAsyncThunk } from "@reduxjs/toolkit";

import * as shippingAPI from "../../../api/shipping";
import { IBasket } from "../../../models/catalogue";
import { reloadBasket } from "../../../store/basket/thunks";
import { IReduxState } from "../reducers.interfaces";
import { getCountryCode, getShippingAddress } from "../utils";

/**
 * Save the selected shipping method for checkout.
 *
 * This thunk saves the user's selected shipping method (e.g., standard,
 * white glove) to the checkout session and reloads the basket to
 * recalculate shipping costs and shipping-dependent offers.
 *
 * @example
 * // Save selected shipping method
 * dispatch(saveShippingMethod());
 *
 * @sideEffects
 * - Reads shipping address from `state.form.shipping_*` fields
 * - Reads shipping method code from `state.form.shipping_method`
 * - Converts country URL to country code
 * - Makes API call to save shipping method via shippingAPI.saveShippingMethod
 * - Dispatches reloadBasket thunk to reload basket with updated shipping costs
 * - Updates `data.basket` with reloaded basket via extraReducers
 * - May trigger offer recalculations on the backend
 */
export const saveShippingMethod = createAsyncThunk(
    "checkout/saveShippingMethod",
    async (_, { getState, dispatch }): Promise<IBasket | null> => {
        const state = getState() as { checkout: IReduxState };
        const shippingAddress = {
            ...getShippingAddress(state.checkout),
            country: getCountryCode(getShippingAddress(state.checkout).country),
        };
        const methodCode = state.checkout.form.shipping_method;
        await shippingAPI.saveShippingMethod(shippingAddress, methodCode);
        // Reload basket to get updated basket offers after shipping method changes
        const result = await dispatch(reloadBasket()).unwrap();
        return result.basket;
    },
);
